/**
 * Convert recipe line quantities into inventory base units (product.unit).
 * Pure — no lot / inventory access.
 *
 * Supported pairs:
 * - kg ↔ g
 * - liter / l ↔ ml
 * - box / carton / pack ↔ piece / pcs / unit (via itemsPerBox or conversionToStock)
 */

const MASS_IN_GRAMS = Object.freeze({
  kg: 1000,
  g: 1,
  gr: 1,
  gram: 1,
  grams: 1,
  kilogram: 1000,
  kilograms: 1000
});

const VOLUME_IN_ML = Object.freeze({
  l: 1000,
  liter: 1000,
  liters: 1000,
  litre: 1000,
  litres: 1000,
  ml: 1,
  milliliter: 1,
  millilitre: 1,
  milliliters: 1,
  millilitres: 1
});

const BOX_LIKE = new Set(['box', 'carton', 'pack', 'case', 'paquet', 'boite', 'boîte']);
const PIECE_LIKE = new Set(['pcs', 'pc', 'piece', 'pieces', 'pièce', 'pièces', 'unit', 'units', 'u', 'pce']);

/**
 * @param {unknown} unit
 * @returns {string}
 */
export function normalizeUnitKey(unit) {
  return String(unit || '')
    .trim()
    .toLowerCase()
    .replace(/\./g, '');
}

/**
 * Explicit conversion factor on the recipe line:
 * inventoryQty = recipeQty * conversionToStock
 * (recipe qty expressed in line unit → product stock unit amount)
 *
 * @param {number} quantity
 * @param {number|null|undefined} conversionToStock
 * @returns {number|null}
 */
function applyExplicitConversion(quantity, conversionToStock) {
  if (conversionToStock == null || conversionToStock === '') return null;
  const factor = Number(conversionToStock);
  if (!Number.isFinite(factor) || factor <= 0) return null;
  return quantity * factor;
}

/**
 * @param {string} from
 * @param {string} to
 * @param {number} quantity
 * @returns {number|null}
 */
function convertMassOrVolume(from, to, quantity) {
  const fromMass = MASS_IN_GRAMS[from];
  const toMass = MASS_IN_GRAMS[to];
  if (fromMass != null && toMass != null) {
    return (quantity * fromMass) / toMass;
  }
  const fromVol = VOLUME_IN_ML[from];
  const toVol = VOLUME_IN_ML[to];
  if (fromVol != null && toVol != null) {
    return (quantity * fromVol) / toVol;
  }
  return null;
}

/**
 * Box/carton ↔ piece using itemsPerBox (pieces per box).
 * @param {string} from
 * @param {string} to
 * @param {number} quantity
 * @param {number|null} itemsPerBox
 * @returns {number|null}
 */
function convertBoxPiece(from, to, quantity, itemsPerBox) {
  const fromBox = BOX_LIKE.has(from);
  const toBox = BOX_LIKE.has(to);
  const fromPiece = PIECE_LIKE.has(from);
  const toPiece = PIECE_LIKE.has(to);
  const perBox = Number(itemsPerBox);
  if (!Number.isFinite(perBox) || perBox <= 0) return null;

  if (fromBox && toPiece) return quantity * perBox;
  if (fromPiece && toBox) return quantity / perBox;
  if (fromBox && toBox) return quantity;
  if (fromPiece && toPiece) return quantity;
  return null;
}

/**
 * Inventory base unit of a catalogue product (stock UOM).
 * @param {object|null|undefined} product
 * @returns {string|null}
 */
export function resolveProductStockUnit(product) {
  if (!product || typeof product !== 'object') return null;
  const unit = product.unit || product.stockUnit || product.baseUnit || product.inventoryUnit;
  const key = normalizeUnitKey(unit);
  return key || null;
}

/**
 * Force a product ingredient line into stock units (no conversion factor).
 * Quantity is already expressed per inventory unit (e.g. kg of meat per batch).
 *
 * @param {object} line — recipe ingredient-like
 * @param {object|null|undefined} product
 * @returns {object}
 */
export function alignIngredientLineToStockUnit(line, product) {
  if (!line || typeof line !== 'object') return line;
  if (line.kind === 'sub_recipe' || line.kind === 'subRecipe') return line;
  const stockUnit = resolveProductStockUnit(product);
  return {
    ...line,
    unit: stockUnit,
    conversionToStock: null
  };
}

/**
 * Sum remaining lot qty for a product (inventory units).
 * @param {unknown} lots
 * @param {string|number} productId
 * @returns {number}
 */
export function sumAvailableInventoryUnits(lots, productId) {
  if (productId == null || productId === '') return 0;
  const list = Array.isArray(lots)
    ? lots
    : lots && typeof lots === 'object'
      ? Object.values(lots)
      : [];
  let total = 0;
  const target = String(productId);
  for (const lot of list) {
    if (!lot?.productId) continue;
    if (String(lot.productId) !== target) continue;
    const rem = Number(lot.remainingQuantity);
    if (Number.isFinite(rem) && rem > 0) total += rem;
  }
  return total;
}

/**
 * How many sale portions the stock can cover from one ingredient line.
 * Line quantity is the amount for recipe.portions (batch), in stock units.
 *
 * @param {{ quantity: number, preparationLoss?: number }} line
 * @param {number} recipePortions
 * @param {number} availableStock
 * @returns {number|null} max sale portions, or null if not computable
 */
export function estimatePortionsFromIngredientStock(line, recipePortions, availableStock) {
  const qty = Number(line?.quantity);
  const batch = Number(recipePortions) > 0 ? Number(recipePortions) : 1;
  if (!Number.isFinite(qty) || qty <= 0) return null;
  const loss = Number(line?.preparationLoss) || 0;
  const lossFactor = loss > 0 && loss < 1 ? 1 / (1 - loss) : loss >= 1 && loss < 100 ? 1 / (1 - loss / 100) : 1;
  // Per one sale portion: (batch_qty / batch_portions) * loss factor
  const perSalePortion = (qty / batch) * lossFactor;
  if (!(perSalePortion > 0)) return null;
  const avail = Number(availableStock);
  if (!Number.isFinite(avail) || avail < 0) return null;
  return Math.floor(avail / perSalePortion + 1e-9);
}

/**
 * Convert a recipe line quantity into inventory base quantity.
 *
 * Resolution order:
 * 1. Explicit conversionToStock (always wins when valid)
 * 2. Same unit → quantity as-is
 * 3. Mass / volume tables
 * 4. Box ↔ piece via product.itemsPerBox / unitsPerPack
 * 5. Fallback: quantity as-is (same-scale assumed)
 *
 * @param {object} params
 * @param {number} params.quantity
 * @param {string|null|undefined} params.fromUnit — recipe line unit
 * @param {string|null|undefined} params.productUnit — product inventory unit
 * @param {number|null|undefined} params.conversionToStock
 * @param {number|null|undefined} params.itemsPerBox
 * @param {object|null|undefined} params.product — optional product for unit / pack
 * @returns {number}
 */
export function toInventoryQuantity({
  quantity,
  fromUnit = null,
  productUnit = null,
  conversionToStock = null,
  itemsPerBox = null,
  product = null
} = {}) {
  const qty = Number(quantity);
  if (!Number.isFinite(qty) || qty <= 0) return 0;

  const explicit = applyExplicitConversion(qty, conversionToStock);
  if (explicit != null) return explicit;

  const from = normalizeUnitKey(fromUnit);
  const to = normalizeUnitKey(
    productUnit || product?.unit || product?.stockUnit || product?.baseUnit || ''
  );

  if (!from || !to || from === to) {
    return qty;
  }

  const massVol = convertMassOrVolume(from, to, qty);
  if (massVol != null) return massVol;

  const pack =
    itemsPerBox != null
      ? Number(itemsPerBox)
      : Number(product?.itemsPerBox ?? product?.unitsPerPack ?? product?.pcsPerBox);
  const boxPiece = convertBoxPiece(from, to, qty, Number.isFinite(pack) ? pack : null);
  if (boxPiece != null) return boxPiece;

  // Unknown pair: treat line already in stock units (caller should set conversionToStock).
  return qty;
}

/**
 * Convenience: convert with only units known.
 * @param {number} quantity
 * @param {string} fromUnit
 * @param {string} toUnit
 * @param {{ conversionToStock?: number, itemsPerBox?: number }} [opts]
 * @returns {number}
 */
export function convertUnit(quantity, fromUnit, toUnit, opts = {}) {
  return toInventoryQuantity({
    quantity,
    fromUnit,
    productUnit: toUnit,
    conversionToStock: opts.conversionToStock,
    itemsPerBox: opts.itemsPerBox
  });
}
